import {
	useState,
	useEffect,
	useCallback,
} from "react";
import * as Sentry from "@sentry/browser";
import { useQuery } from "@apollo/client";
import get from "lodash/get";

import useBoostReport from "apps/notifications_footer/util/useBoostReport";
import useAutoDismissing from "apps/notifications_footer/util/useAutoDismissing";
import {
	getSeenNotifications,
	setSeenNotifications,
	generateNotificationId,
	separateBoostNotifications,
} from "apps/notifications_footer/util/helpers";
import {
	GET_EVENT_DATA_QUERY,
} from "apps/notifications_footer/util/fragments";

// How often to get notifications; changes if Boosting.
export const DEFAULT_REFETCH_INTERVAL = 1000 * 60;
export const BOOST_REFETCH_INTERVAL = 1000 * 10;

// Hide dismissable notifications after 5s.
export const AUTO_DISMISS_TIMEOUT = 1000 * 5;

// Record of notifications we've shown before.
const SEEN_LAST_SESSION = getSeenNotifications();

// Process notifications, sets them up to be autodismissed as necessary.
function useNotifications({
	userid,
	shouldAutoDismissNotifications = true,
	autoDismissTimeout = AUTO_DISMISS_TIMEOUT,
	refetchInterval = DEFAULT_REFETCH_INTERVAL,
} = {}) {
	// Interval at which to refetch notification data.
	const [pollInterval, setPollInterval] = useState(refetchInterval);

	// Fetch & extract user notification info.
	const [hasEncounteredError, setHasEncounteredError] = useState(false);
	const { data, error } = useQuery(GET_EVENT_DATA_QUERY, {
		variables: {
			userid,
		},
		pollInterval,
		skip: hasEncounteredError,
	});
	useEffect(() => {
		// We're good! (or already bad)
		if (!error || hasEncounteredError) {
			return;
		}

		// User encountered an error. Stop polling.
		setHasEncounteredError(true);
		setPollInterval(0);
		Sentry.captureException(error);
	}, [error, hasEncounteredError]);

	const activeBoost = get(data, "user.activeBoost");
	const lastBoost = get(data, "user.lastBoost");
	const rawNotifications = get(data, "user.notifications") || [];
	const [boostActivity, nonBoostActivity] = separateBoostNotifications(
		rawNotifications,
	);
	const isBoostReport = boostActivity.length > 0;

	// Change the Poll Interval if we're Boosting.
	const currentPollInterval = activeBoost ? BOOST_REFETCH_INTERVAL : refetchInterval;
	useEffect(() => setPollInterval(currentPollInterval), [currentPollInterval]);

	// List of hidden notifications. Default to cache from last session, in case those show up again.
	// Whenever the hidden list changes, update the localStorage cache.
	const [hiddenNotifications, setHiddenNotifications] = useState(SEEN_LAST_SESSION);
	const hideNotification = useCallback((notificationId) => {
		setHiddenNotifications((hiddenSoFar) => [...hiddenSoFar, notificationId]);
	}, []);
	useEffect(() => setSeenNotifications(hiddenNotifications), [hiddenNotifications]);

	// Add some additional metadata to the notifications.
	const notifications = (isBoostReport ? boostActivity : nonBoostActivity)
		.map((notification) => {
			const id = generateNotificationId(notification);
			const isHidden = hiddenNotifications.includes(id);
			const hide = () => hideNotification(id);

			return {
				...notification,
				id,
				isHidden,
				hide,
			};
		});

	// Auto-dissmisses notifications after a given timeout.
	useAutoDismissing(notifications, {
		isEnabled: shouldAutoDismissNotifications,
		timeout: autoDismissTimeout,
	});

	// Handle Boost Report show/hide/exit logic.
	const {
		isBoostReportExpanded,
		toggleBoostReport,
		closeBoostReport,
	} = useBoostReport({
		userid,
		activeBoost,
		lastBoost,
		nonBoostActivity,
		isBoostReport,
	});

	return {
		notifications,

		isBoostReport,
		isBoostReportExpanded,
		toggleBoostReport,
		closeBoostReport,
	};
}

export default useNotifications;



// WEBPACK FOOTER //
// ./src/apps/notifications_footer/util/useNotifications.js